import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, ArrowRight } from "lucide-react";
import { WELCOME_VIDEO_URL, NETWORK } from "../data/content";

export default function IntroVideo() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("ekg_intro_seen");
  });
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  const dismiss = () => {
    sessionStorage.setItem("ekg_intro_seen", "1");
    document.body.style.overflow = "";
    setShow(false);
  };

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.volume = 1;
    setMuted(v.muted);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="intro"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-0 z-[200] bg-black"
        data-testid="intro-video-overlay"
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover md:object-contain bg-black"
          src={WELCOME_VIDEO_URL}
          autoPlay
          muted
          playsInline
          onEnded={dismiss}
        />

        {/* subtle vignette */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/70 via-transparent to-black/40" />

        {/* Brand mark top-left */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="absolute top-6 left-6 md:top-8 md:left-10 flex items-center gap-3"
        >
          <img src={NETWORK.logo} alt="EKG" className="w-10 h-10 rounded-full ring-1 ring-secondary/50" />
          <span className="overline text-white/80 text-[0.6rem] hidden sm:block">{NETWORK.name}</span>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="absolute bottom-8 left-0 right-0 px-6 md:px-10 flex items-center justify-between"
        >
          <button
            onClick={toggleSound}
            data-testid="intro-sound-toggle"
            className="flex items-center gap-3 border border-white/30 text-white/90 overline px-5 py-3 hover:bg-white/10 transition-colors duration-300 backdrop-blur-sm"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            {muted ? "Tap for Sound" : "Sound On"}
          </button>

          <button
            onClick={dismiss}
            data-testid="intro-enter-btn"
            className="group flex items-center gap-3 bg-primary text-primary-foreground overline px-7 py-4 hover:bg-primary/85 transition-colors duration-300"
          >
            Enter Site
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
